interface ErrorMapping {
  pattern: RegExp
  userFriendlyMessage: string
  category: 'network' | 'validation' | 'auth' | 'credits' | 'system' | 'upload'
}

const ERROR_MAPPINGS: ErrorMapping[] = [
  // Network and connection errors
  {
    pattern:
      /could not connect to the server|network connection lost|failed to fetch|connection timeout/i,
    userFriendlyMessage:
      "We're having trouble connecting to our servers. Please check your internet connection and try again.",
    category: 'network',
  },
  {
    pattern: /http error! status: 500/i,
    userFriendlyMessage: 'Our servers are experiencing issues. Please try again in a few moments.',
    category: 'network',
  },
  {
    pattern: /http error! status: 502|http error! status: 503|http error! status: 504/i,
    userFriendlyMessage: 'Our servers are temporarily unavailable. Please try again shortly.',
    category: 'network',
  },
  {
    pattern: /http error! status: 429/i,
    userFriendlyMessage: "You're sending requests too quickly. Please wait a moment and try again.",
    category: 'network',
  },
  {
    pattern: /http error! status: 401/i,
    userFriendlyMessage: 'Your session has expired. Please refresh the page and sign in again.',
    category: 'auth',
  },
  {
    pattern: /http error! status: 403/i,
    userFriendlyMessage: "You don't have permission to perform this action.",
    category: 'auth',
  },
  {
    pattern: /http error! status: 404/i,
    userFriendlyMessage:
      'The requested resource could not be found. Please refresh the page and try again.',
    category: 'network',
  },
  {
    pattern: /session creation failed/i,
    userFriendlyMessage:
      "We couldn't start your chat session. Please refresh the page and try again.",
    category: 'system',
  },
  {
    pattern: /stream connection error|response body is not readable/i,
    userFriendlyMessage:
      'We lost connection while processing your message. Please try sending it again.',
    category: 'network',
  },

  // Validation errors (Zod)
  {
    pattern: /invalid request|zod validation|schema validation|validation failed/i,
    userFriendlyMessage:
      'There was an issue with your request format. Please try again or contact support if this continues.',
    category: 'validation',
  },
  {
    pattern: /required field|missing required/i,
    userFriendlyMessage:
      'Some required information is missing. Please check your input and try again.',
    category: 'validation',
  },

  // File upload errors
  {
    pattern: /file upload failed|no file url returned/i,
    userFriendlyMessage:
      "We couldn't upload your file. Please check the file size and format, then try again.",
    category: 'upload',
  },
  {
    pattern: /file too large|file size exceeded/i,
    userFriendlyMessage: 'Your file is too large. Please choose a smaller file and try again.',
    category: 'upload',
  },
  {
    pattern: /unsupported file type|invalid file format/i,
    userFriendlyMessage: "This file type isn't supported. Please choose a different file format.",
    category: 'upload',
  },

  // Credit and billing errors
  {
    pattern: /insufficient credits|credit limit|billing|payment required/i,
    userFriendlyMessage:
      "You've reached your usage limit. Please upgrade your plan or wait until your credits refresh.",
    category: 'credits',
  },
  {
    pattern: /quota exceeded|rate limit/i,
    userFriendlyMessage:
      "You've reached your usage limit for now. Please try again later or upgrade your plan.",
    category: 'credits',
  },

  // Duplicate and concurrency errors
  {
    pattern: /duplicate message detected/i,
    userFriendlyMessage:
      'This message was already sent. This usually happens when the page refreshes or you click send multiple times quickly.',
    category: 'system',
  },
  {
    pattern: /operation already in progress|concurrent request/i,
    userFriendlyMessage:
      'Another operation is already running. Please wait for it to complete before trying again.',
    category: 'system',
  },

  // Workflow and processing errors
  {
    pattern: /workflow failed|execution failed|processing error/i,
    userFriendlyMessage:
      'We encountered an issue while processing your request. Please try again or simplify your request.',
    category: 'system',
  },

  // Google Sheets and CSV errors
  {
    pattern: /file is too large to be exported|export.*size.*limit.*exceeded/i,
    userFriendlyMessage:
      "The file you're trying to process is too large. Please try using a smaller file or break it into smaller sections.",
    category: 'validation',
  },
  {
    pattern: /failed to process google sheet|google.*sheet.*error/i,
    userFriendlyMessage:
      "We couldn't process your Google Sheet. Please check your permissions and try again.",
    category: 'system',
  },
  {
    pattern: /csv.*processing.*failed|csv.*reader.*error/i,
    userFriendlyMessage:
      "We couldn't read your CSV file. Please check the file format and try again.",
    category: 'validation',
  },

  // Tool-specific errors
  {
    pattern: /external service is currently unavailable/i,
    userFriendlyMessage:
      'The service we need to complete this task is temporarily unavailable. Please try again in a few minutes.',
    category: 'system',
  },
  {
    pattern: /node.*execution.*failed|tool.*execution.*failed/i,
    userFriendlyMessage:
      'One of the steps in your workflow failed. Please check your inputs and try again.',
    category: 'system',
  },
  {
    pattern: /timeout|request timeout|operation timed out/i,
    userFriendlyMessage:
      'Your request is taking longer than expected. Please try again with a simpler request.',
    category: 'system',
  },

  // Generic fallbacks
  {
    pattern: /abort|aborted|cancelled/i,
    userFriendlyMessage: 'The operation was cancelled. Please try again.',
    category: 'system',
  },
]

export interface ErrorMappingResult {
  originalError: string
  userFriendlyMessage: string
  category: ErrorMapping['category']
  shouldShowRetry: boolean
  isTransient: boolean
}

/**
 * Converts technical error messages to user-friendly ones using regex pattern matching
 */
export function mapErrorToUserFriendly(error: string): ErrorMappingResult {
  // Handle empty or null errors
  if (!error || error.trim() === '') {
    return {
      originalError: error,
      userFriendlyMessage: 'Something unexpected happened. Please try again.',
      category: 'system',
      shouldShowRetry: true,
      isTransient: true,
    }
  }

  // Find the first matching pattern
  for (const mapping of ERROR_MAPPINGS) {
    if (mapping.pattern.test(error)) {
      return {
        originalError: error,
        userFriendlyMessage: mapping.userFriendlyMessage,
        category: mapping.category,
        shouldShowRetry: shouldShowRetryButton(mapping.category),
        isTransient: isTransientError(mapping.category),
      }
    }
  }

  // Fallback for unmapped errors
  return {
    originalError: error,
    userFriendlyMessage: getGenericErrorMessage(error),
    category: 'system',
    shouldShowRetry: true,
    isTransient: true,
  }
}

/**
 * Determines if a retry button should be shown based on error category
 */
function shouldShowRetryButton(category: ErrorMapping['category']): boolean {
  switch (category) {
    case 'network':
    case 'system':
      return true
    case 'validation':
    case 'upload':
      return false // User needs to fix their input first
    case 'auth':
      return false // User needs to re-authenticate
    case 'credits':
      return false // User needs to upgrade/wait
    default:
      return true
  }
}

/**
 * Determines if an error is likely to be transient (temporary)
 */
function isTransientError(category: ErrorMapping['category']): boolean {
  switch (category) {
    case 'network':
    case 'system':
      return true
    case 'validation':
    case 'upload':
    case 'auth':
    case 'credits':
      return false
    default:
      return true
  }
}

/**
 * Generates a generic user-friendly message for unmapped errors
 */
function getGenericErrorMessage(error: string): string {
  // For very technical errors, provide a generic message
  if (
    error.includes('stack trace') ||
    error.includes('TypeError') ||
    error.includes('ReferenceError')
  ) {
    return 'We encountered a technical issue. Please try again or contact support if this continues.'
  }

  // For unknown but potentially user-facing errors, sanitize and show them
  const sanitized = error
    .replace(/\[.*?\]/g, '') // Remove bracketed technical info like [CopilotService]
    .replace(/Error:\s*/i, '') // Remove "Error:" prefix
    .trim()

  if (sanitized.length > 200) {
    return 'We encountered an issue processing your request. Please try again or contact support if this continues.'
  }

  // Capitalize first letter if needed
  return sanitized.charAt(0).toUpperCase() + sanitized.slice(1)
}

/**
 * Get a user-friendly title based on error category
 */
export function getErrorTitle(category: ErrorMapping['category']): string {
  switch (category) {
    case 'network':
      return 'Connection Issue'
    case 'validation':
      return 'Invalid Input'
    case 'auth':
      return 'Authentication Required'
    case 'credits':
      return 'Usage Limit Reached'
    case 'upload':
      return 'File Upload Failed'
    case 'system':
    default:
      return 'Something went wrong'
  }
}

/**
 * Get appropriate retry button text based on error category
 */
export function getRetryButtonText(category: ErrorMapping['category']): string {
  switch (category) {
    case 'network':
      return 'Continue'
    case 'system':
      return 'Continue'
    case 'validation':
    case 'upload':
      return 'Fix and Continue'
    case 'auth':
      return 'Sign In Again'
    case 'credits':
      return 'Upgrade Plan'
    default:
      return 'Retry'
  }
}

/**
 * Extract error message from nodeExecution.outputs structure
 * Handles complex error objects with nested messages and failedNodes
 */
export function extractErrorFromNodeExecution(outputs: any): string {
  if (!outputs) {
    return 'Unknown error occurred'
  }

  // If outputs is a string, return it directly
  if (typeof outputs === 'string') {
    return outputs
  }

  // If outputs is an object, try to extract meaningful error messages
  if (typeof outputs === 'object') {
    // Look for common error message patterns
    const errorMessage = outputs.message || outputs.error || outputs.errorMessage

    if (errorMessage) {
      return errorMessage
    }

    // Handle failedNodes structure (from the user's example)
    if (outputs.failedNodes && Array.isArray(outputs.failedNodes)) {
      const firstFailedNode = outputs.failedNodes[0]
      if (firstFailedNode && firstFailedNode.error) {
        return firstFailedNode.error
      }
    }

    // If it's a complex object, try to stringify and extract meaningful parts
    try {
      const stringified = JSON.stringify(outputs)

      // Try to extract error message from JSON string using regex patterns
      const messageMatch = stringified.match(/"message"\s*:\s*"([^"]+)"/i)
      if (messageMatch && messageMatch[1]) {
        return messageMatch[1]
      }

      const errorMatch = stringified.match(/"error"\s*:\s*"([^"]+)"/i)
      if (errorMatch && errorMatch[1]) {
        return errorMatch[1]
      }

      // For very complex objects, return a simplified version
      if (stringified.length > 500) {
        return 'A workflow step encountered an error. Please try again or contact support.'
      }

      return stringified
    } catch {
      return 'Error processing failed step'
    }
  }

  return String(outputs)
}
